import React, {useRef, useState} from 'react';

import {useStore} from 'effector-react';
import i18next from 'i18next';

import {useTranslation} from 'react-i18next';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Modal} from 'components/ui-kit/Modal';
import {DocumentStatus} from 'core/dictionaries';
import {setTargetStatus} from 'models/documents/invoices/common';
import {
    $invoiceLinesHolder,
    $taxesAndTotal,
    addInvoiceLineValues,
    setForceAllowToSend,
    updateAllDataFromInvoiceLinesHolder,
    updateTaxesAndTotal,
} from 'models/documents/invoices/create';

import {$invoiceCreatingPending} from 'models/documents/invoices/details';
import {TRANSPORT_PACKAGE_TYPES} from 'types/gtins';

import {combineSiblingUnitsByGtin} from '../../InvoiceNew/hooks/useCodeScanner';
import {processInvoiceLinesWithCombinedCodes} from '../../InvoiceNew/Pages/InvoiceLines';

import {ButtonsContainer, Description, Root} from './styled';

export const GroupLinesByGtinModal = ({open, onClose}: {open: boolean; onClose: () => void}) => {
    const {t: translation} = useTranslation();
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const invoiceCreatingPending = useStore($invoiceCreatingPending);
    const GEN_INFO = 'pages.documents.invoices.create.grouping';
    const t = (key: string) => translation(`${GEN_INFO}.${key}`);

    const handleGroupLinesByGtin = () => {
        setTargetStatus(DocumentStatus['UNDER_APPROVAL']);
        const fullfilledResponsesWithGroup = invoiceLinesHolder.reduce((acc, item, index) => {
            if (item.packagingType && TRANSPORT_PACKAGE_TYPES.includes(item.packagingType)) {
                const {subRows, code, ...rest} = item;

                if (item.isCombined || !subRows?.length) {
                    return {
                        ...acc,
                        [code]: {
                            ...item,
                            code,
                        },
                    };
                }

                return {
                    ...acc,
                    [code]: {
                        ...rest,
                        code,
                        isCombined: true,
                        subRows: combineSiblingUnitsByGtin(subRows),
                    },
                };
            }
            const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
            const {gtin, price, charges, vat, serviceCategory, vatCategory, code} = item;
            const {isCharge, amountXaf, reason} = charges ?? {};

            const hashKeyObject = {
                gtin,
                price,
                charges: isCharge,
                rate: amountXaf,
                reason,
                vat,
                category: serviceCategory?.value,
            };

            const hashKey = JSON.stringify(hashKeyObject);

            if (acc[hashKey]) {
                const addCharges = acc[hashKey]['charges']
                    ? toLowerAndTrim(acc[hashKey]['charges'].isCharge) === 'true'
                        ? Number(acc[hashKey].charges.amountXaf)
                        : -Number(acc[hashKey].charges.amountXaf)
                    : 0;

                const qty =
                    Number(acc[hashKey]['qty']) +
                    (item['combinedCodes']?.length ? item['combinedCodes']?.length : 1);
                const lines = qty * Number(acc[hashKey]['price']) + addCharges;
                const lineAmountWVat = (Number(vat) / 100) * lines + lines;

                acc[hashKey] = {
                    ...acc[hashKey],
                    qty: qty.toString(),
                    lines: !price || !qty ? '' : lines.toFixed(2),
                    lineAmountWVat: !price || !qty ? '' : lineAmountWVat.toFixed(2),
                    combinedCodes: [
                        ...acc[hashKey]['combinedCodes'],
                        ...(item['combinedCodes']?.length ? item['combinedCodes'] : [code]),
                    ],
                };
                return acc;
            }

            if (!item.isMarkedItem) {
                const unmarkedHashKey = `unmarked-${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2)}`;

                return {
                    ...acc,
                    [unmarkedHashKey]: {
                        ...item,
                    },
                };
            }

            return {
                ...acc,
                [hashKey]: {
                    ...item,
                    combinedCodes: item['combinedCodes']?.length
                        ? [...item['combinedCodes']]
                        : [code],
                },
            };
        }, {} as Record<string, any>);

        const processedLines = processInvoiceLinesWithCombinedCodes(
            Object.values(fullfilledResponsesWithGroup),
        );

        setForceAllowToSend(true);
        updateAllDataFromInvoiceLinesHolder(Object.values(fullfilledResponsesWithGroup));
        addInvoiceLineValues(processedLines);
    };

    const sendWithoutGrouping = () => {
        setTargetStatus(DocumentStatus['UNDER_APPROVAL']);
        updateTaxesAndTotal({
            data: $taxesAndTotal.getState().data,
            allowToSend: true,
        });
        onClose();
    };

    return (
        <Modal show={open} onClose={onClose} title={t('sendInvoice')} modalWidth={0}>
            <Root>
                <Description>{t('sendInvoiceTitle')}</Description>
                <ButtonsContainer>
                    <Button
                        type="button"
                        onClick={sendWithoutGrouping}
                        variant={ButtonVariant.Outline}
                        disabled={invoiceCreatingPending}
                    >
                        {t('send')}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleGroupLinesByGtin}
                        variant={ButtonVariant.Normal}
                        disabled={invoiceCreatingPending}
                    >
                        {t('groupUp')}
                    </Button>
                </ButtonsContainer>
            </Root>
        </Modal>
    );
};
